import React, { useEffect, useState, useRef, useMemo, useCallback, useLayoutEffect } from 'react';

import { isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Content,
  Table,
  Loader,
  Message,
  Modal,
  Panel,
  Input,
  CheckPicker,
  RadioGroup,
  Radio,
  Button,
  FlexboxGrid,
  useToaster,
} from 'rsuite';
import ArrowRightIcon from '@rsuite/icons/ArrowRight';
import ArrowDownIcon from '@rsuite/icons/ArrowDown';
import EditIcon from '@rsuite/icons/Edit';

import NavigationBar from '../Shared/NavigationBar';
import SearchBar from '../Shared/SearchBar';
import AddToCollectionModal from './AddToCollectionModal';
import EditEntryModal from './EditEntryModal';
import authFetch, { API_BASE } from '../../helpers/authFetch';
import useDebouncedSearch from '../../helpers/useDebouncedSearch';
import { COLOR_OPTIONS, RARITY_OPTIONS } from '../../helpers/filterConstants';
import { PROXY_BADGE_COLOR, FINISH_LABELS, FINISH_COLORS, CONDITION_LABELS, CONDITION_COLORS } from './CardAttributeDefaults';
import './CollectionDetail.css';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';

const { Column, HeaderCell, Cell } = Table;

const SPACING = {
  containerMaxWidth: 1100,
  containerPadding: 16,
  containerMarginTop: 30,
  headerMarginBottom: 20,
  statsGap: 16,
  filterGap: 12,
  filterMarginBottom: 12,
  filterPaddingV: 10,
  searchWidth: isMobile ? '100%' : 200,
  pickerWidth: isMobile ? '100%' : 160,
  collectionPickerWidth: isMobile ? '100%' : 200,
  columnPickerWidth: isMobile ? '100%' : 140,
  filterCountFontSize: 13,
  statFontSize: isMobile ? 18 : 24,
  thumbnailSize: 32,
  expandedPadding: 12,
  expandedRowHeight: 36,
  expandedHeaderHeight: 28,
  locationBadgePaddingH: 6,
  locationBadgePaddingV: 2,
  locationBadgeRadius: 4,
  locationBadgeFontSize: 11,
  locationDetailFontSize: 12,
  editIconSize: 16,
};

const LOCATION_BADGE_COLORS = {
  DECK: '#3498db',
  TRADE_BINDER: '#2ecc71',
  WISHLIST: '#9b59b6',
};

const LOCATION_BADGE_LABELS = {
  DECK: 'Deck',
  TRADE_BINDER: 'Binder',
  WISHLIST: 'Wish List',
};

const buildColors = (theme) => {
  const t = getThemeColors(theme);
  return {
    muted: t.textMuted,
    gain: '#2ecc71',
    link: t.accentSecondary,
    expandedBg: '#1a1a1a',
    locationRowBorder: '#333',
  };
};

const getUsdPrice = (prices, finish) => {
  if (!prices) return null;
  if (finish === 'foil') return prices.usd_foil || prices.usd || null;
  if (finish === 'etched') return prices.usd_etched || prices.usd || null;
  return prices.usd || null;
};

const PAGE_SIZE = 500;
const SCRYFALL_CHUNK_SIZE = 75;
const PARALLEL_PAGE_LIMIT = 3;
const ENRICH_BATCH_SIZE = 200;
const TABLE_ROW_HEIGHT = 46;
const TABLE_TOP_OFFSET = 250;
const EXPANDED_ROW_PADDING = 24;
const LOCALSTORAGE_COLUMNS_KEY = 'mtg_grand_collection_columns';
const LOCALSTORAGE_SORT_KEY = 'mtg_grand_collection_sort';
const DEFAULT_SORT = { column: 'name', type: 'asc' };

const OPTIONAL_DESKTOP_COLUMNS = [
  { key: 'image',            label: '',          width: 40,              pickerLabel: 'Image' },
  { key: 'set_name',         label: 'Set',       flexGrow: 1, sortable: true, pickerLabel: 'Set' },
  { key: 'collector_number', label: '#',         width: 60,  sortable: true, pickerLabel: 'Collector #' },
  { key: 'finish',           label: 'Finish',    width: 90,  sortable: true, pickerLabel: 'Finish' },
  { key: 'real_quantity',    label: 'Real Qty',  width: 90,  sortable: true, pickerLabel: 'Real Qty' },
  { key: 'proxy_quantity',   label: 'Proxy Qty', width: 90,  sortable: true, pickerLabel: 'Proxy Qty' },
  { key: 'locations_count',  label: 'Locations', width: 90,  sortable: true, pickerLabel: 'Locations' },
  { key: 'market_price',     label: 'Price',     width: 90,  sortable: true, pickerLabel: 'Market Price' },
  { key: 'estimated_value',  label: 'Est. Value', flexGrow: 1, sortable: true, pickerLabel: 'Est. Value' },
];

const COLUMN_PICKER_DATA = OPTIONAL_DESKTOP_COLUMNS.map(c => ({ label: c.pickerLabel, value: c.key }));

const MOBILE_COLUMNS = [
  { key: 'finish',        label: 'Finish',   width: 80, sortable: true },
  { key: 'real_quantity', label: 'Real Qty', width: 80, sortable: true },
];

const loadHiddenColumns = () => {
  try {
    const stored = localStorage.getItem(LOCALSTORAGE_COLUMNS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return new Set(parsed);
    }
  } catch { /* ignore */ }
  return new Set();
};

const saveHiddenColumns = (hidden) => {
  try {
    localStorage.setItem(LOCALSTORAGE_COLUMNS_KEY, JSON.stringify([...hidden]));
  } catch { /* ignore */ }
};

const loadSort = () => {
  try {
    const stored = localStorage.getItem(LOCALSTORAGE_SORT_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed.column === 'string' && (parsed.type === 'asc' || parsed.type === 'desc')) {
        return parsed;
      }
    }
  } catch { /* ignore */ }
  return DEFAULT_SORT;
};

const saveSort = (column, type) => {
  try {
    localStorage.setItem(LOCALSTORAGE_SORT_KEY, JSON.stringify({ column, type }));
  } catch { /* ignore */ }
};

const GrandCollection = () => {
  const { preferences } = usePreferences();
  const COLORS = useMemo(() => buildColors(preferences.theme), [preferences.theme]);
  const navigate = useNavigate();
  const toaster = useToaster();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [tableData, setTableData] = useState([]);
  const [allLoaded, setAllLoaded] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState({ done: 0, total: 0 });
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [paginationProgress, setPaginationProgress] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [nameSearch, setNameSearch] = useState('');
  const [colorFilter, setColorFilter] = useState([]);
  const [rarityFilter, setRarityFilter] = useState([]);
  const [collectionFilter, setCollectionFilter] = useState([]);
  const [proxyFilter, setProxyFilter] = useState('all');
  const [collections, setCollections] = useState([]);
  const [sort, setSort] = useState(() => loadSort());
  const { column: sortColumn, type: sortType } = sort;
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addCardData, setAddCardData] = useState(null);
  const [myCollectionId, setMyCollectionId] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [editCollectionMeta, setEditCollectionMeta] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState(() => loadHiddenColumns());

  const priceCacheRef = useRef({});
  const isMountedRef = useRef(true);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  const [searchInput, setSearchInput] = useDebouncedSearch(nameSearch, useCallback((val) => setNameSearch(val), []));

  useEffect(() => {
    return () => { isMountedRef.current = false; };
  }, []);

  useLayoutEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const enrichEntries = (entries, cards) => entries.map((entry, idx) => {
    const card = cards[idx];
    const finish = entry.finish || 'nonfoil';

    const usdPrice = getUsdPrice(card?.prices, finish);

    const currentRaw = usdPrice ? Number(usdPrice) : null;
    const realQty = entry.total_real_quantity || 0;
    const proxyQty = entry.total_proxy_quantity || 0;
    const locationsCount = (entry.locations || []).length;
    const estimatedRaw = currentRaw != null ? currentRaw * realQty : null;
    const rawColors = card?.colors || [];

    return {
      ...entry,
      row_key: `${entry.scryfall_id}_${finish}`,
      finish,
      name: card?.name || 'Unknown Card',
      set_name: card?.set_name || '',
      collector_number: card?.collector_number || '',
      rarity: card?.rarity || '',
      colors_raw: rawColors,
      real_quantity: realQty,
      proxy_quantity: proxyQty,
      locations_count: locationsCount,
      market_price: currentRaw != null ? `$${currentRaw.toFixed(2)}` : '',
      market_price_raw: currentRaw,
      estimated_value: estimatedRaw != null ? `$${estimatedRaw.toFixed(2)}` : '',
      estimated_value_raw: estimatedRaw,
      card_object: card,
    };
  });

  const fetchRawPage = async (pageNum) => {
    const res = await authFetch(`/collections/grand?page=${pageNum}&limit=${PAGE_SIZE}`);
    if (!res.ok) throw new Error('Failed to fetch collection');
    return res.json();
  };

  const enrichBatch = async (rawEntries) => {
    const cache = priceCacheRef.current;
    const scryfallIds = rawEntries.map(c => c.scryfall_id);
    const uncachedIds = [...new Set(scryfallIds)].filter((sid) => !cache[sid]);

    if (uncachedIds.length > 0) {
      const chunks = [];
      for (let i = 0; i < uncachedIds.length; i += SCRYFALL_CHUNK_SIZE) {
        chunks.push(uncachedIds.slice(i, i + SCRYFALL_CHUNK_SIZE));
      }

      await Promise.all(chunks.map(async (chunk) => {
        try {
          const res = await fetch(`${API_BASE}/cards/collection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ identifiers: chunk.map((sid) => ({ id: sid })) }),
          });
          if (res.ok) {
            const data = await res.json();
            if (data.data) {
              data.data.forEach((card) => { cache[card.id] = card; });
            }
          }
        } catch (err) {
          // batch fetch failed, card will show as unknown
        }
      }));
    }

    const cards = scryfallIds.map((sid) => cache[sid] || null);
    return enrichEntries(rawEntries, cards);
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMore(false);
    setAllLoaded(false);
    setEnriching(false);
    setTableData([]);
    setError('');
    setExpandedRowKeys([]);

    try {
      const firstData = await fetchRawPage(1);
      if (!isMountedRef.current) return;

      const pages = firstData.pagination?.totalPages || 0;
      setTotalPages(pages);
      setPaginationProgress(1);

      let allRawEntries = [...(firstData.cards || [])];

      if (pages > 1) {
        setLoadingMore(true);
        const remainingPages = Array.from({ length: pages - 1 }, (_, i) => i + 2);

        for (let i = 0; i < remainingPages.length; i += PARALLEL_PAGE_LIMIT) {
          if (!isMountedRef.current) break;
          const batch = remainingPages.slice(i, i + PARALLEL_PAGE_LIMIT);
          const results = await Promise.all(batch.map(fetchRawPage));

          for (const result of results) {
            if (result.cards) allRawEntries = allRawEntries.concat(result.cards);
          }
          setPaginationProgress(Math.min(i + PARALLEL_PAGE_LIMIT + 1, pages));
        }
        setLoadingMore(false);
      }

      if (!isMountedRef.current) return;

      setLoading(false);
      setEnriching(true);

      for (let i = 0; i < allRawEntries.length; i += ENRICH_BATCH_SIZE) {
        if (!isMountedRef.current) break;
        const slice = allRawEntries.slice(i, i + ENRICH_BATCH_SIZE);
        const enriched = await enrichBatch(slice);

        if (!isMountedRef.current) break;
        setTableData(prev => [...prev, ...enriched]);
      }

      setEnriching(false);
      setAllLoaded(true);
    } catch (err) {
      if (isMountedRef.current) {
        setError(err.message || 'Failed to load collection');
        setLoading(false);
        setLoadingMore(false);
        setEnriching(false);
      }
    }
  };

  useEffect(() => {
    fetchAllData();
    const fetchCollections = async () => {
      try {
        const res = await authFetch('/collections');
        if (res.ok) {
          const data = await res.json();
          if (isMountedRef.current) setCollections(data);
        }
      } catch (err) {
        // non-fatal
      }
    };
    fetchCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const collectionOptions = useMemo(() =>
    collections.map((c) => ({ label: c.name, value: c.id })),
    [collections]
  );

  const filteredData = useMemo(() => {
    let data = tableData;

    if (nameSearch) {
      const term = nameSearch.toLowerCase();
      data = data.filter(card => card.name.toLowerCase().includes(term));
    }

    if (colorFilter.length > 0) {
      data = data.filter((row) => {
        const raw = row.colors_raw || [];
        return colorFilter.some((f) => {
          if (f === 'C') return raw.length === 0;
          if (f === 'M') return raw.length > 1;
          return raw.includes(f);
        });
      });
    }

    if (rarityFilter.length > 0) {
      data = data.filter((row) => rarityFilter.includes(row.rarity));
    }

    if (collectionFilter.length > 0) {
      data = data.filter((row) => {
        const locationIds = (row.locations || []).map((loc) => loc.collection_id);
        return collectionFilter.some((id) => locationIds.includes(id));
      });
    }

    if (proxyFilter === 'real') {
      data = data.filter((row) => (row.real_quantity || 0) > 0);
    } else if (proxyFilter === 'proxy') {
      data = data.filter((row) => (row.proxy_quantity || 0) > 0);
    }

    return data;
  }, [tableData, nameSearch, colorFilter, rarityFilter, collectionFilter, proxyFilter]);

  const hasActiveFilters =
    nameSearch ||
    colorFilter.length > 0 ||
    rarityFilter.length > 0 ||
    collectionFilter.length > 0 ||
    proxyFilter !== 'all';

  const handleClearFilters = () => {
    setNameSearch('');
    setSearchInput('');
    setColorFilter([]);
    setRarityFilter([]);
    setCollectionFilter([]);
    setProxyFilter('all');
    setSort(DEFAULT_SORT);
    saveSort(DEFAULT_SORT.column, DEFAULT_SORT.type);
  };

  const handleRefreshPrices = async () => {
    if (tableData.length === 0) return;

    const uniqueScryfallIds = [...new Set(tableData.map(e => e.scryfall_id))].filter(Boolean);
    if (uniqueScryfallIds.length === 0) return;

    setShowRefreshModal(false);
    setRefreshing(true);
    setRefreshProgress({ done: 0, total: uniqueScryfallIds.length });

    let updated = 0;
    let skipped = 0;

    for (const cardId of uniqueScryfallIds) {
      try {
        const res = await authFetch(`/cards/${cardId}/refresh`, { method: 'POST' });
        if (res.ok) {
          const refreshedCard = await res.json();
          setTableData(prevData => prevData.map(entry => {
            if (entry.scryfall_id !== cardId) return entry;

            const finish = entry.finish || 'nonfoil';
            let usdPrice;
            if (finish === 'foil') {
              usdPrice = refreshedCard?.prices?.usd_foil || refreshedCard?.prices?.usd || null;
            } else if (finish === 'etched') {
              usdPrice = refreshedCard?.prices?.usd_etched || refreshedCard?.prices?.usd || null;
            } else {
              usdPrice = refreshedCard?.prices?.usd || null;
            }
            const marketPriceRaw = usdPrice ? Number(usdPrice) : null;
            const estimatedValueRaw = marketPriceRaw != null ? marketPriceRaw * (entry.quantity || 1) : null;

            return {
              ...entry,
              market_price: marketPriceRaw != null ? `$${marketPriceRaw.toFixed(2)}` : '',
              market_price_raw: marketPriceRaw,
              estimated_value: estimatedValueRaw != null ? `$${estimatedValueRaw.toFixed(2)}` : '',
              estimated_value_raw: estimatedValueRaw,
              card_object: refreshedCard,
            };
          }));
          updated++;
        } else if (res.status === 429) {
          skipped++;
        }
      } catch (error) {
        console.error('Error refreshing card:', error);
      }
      setRefreshProgress(prev => ({ ...prev, done: prev.done + 1 }));
    }

    setRefreshing(false);
    const message = skipped > 0
      ? `${updated} price${updated !== 1 ? 's' : ''} updated, ${skipped} already current`
      : `${updated} price${updated !== 1 ? 's' : ''} refreshed`;
    toaster.push(
      <Message type="success" showIcon closable>{message}</Message>,
      { placement: 'topCenter', duration: 3000 }
    );
  };

  const sortedData = useMemo(() => {
    if (!sortColumn || !sortType) {
      return filteredData;
    }

    return filteredData.map((row, index) => ({ row, index })).sort(({ row: a, index: ai }, { row: b, index: bi }) => {
      let aVal;
      let bVal;

      if (sortColumn === 'estimated_value') {
        aVal = a.estimated_value_raw ?? -Infinity;
        bVal = b.estimated_value_raw ?? -Infinity;
      } else if (sortColumn === 'market_price') {
        aVal = a.market_price_raw ?? -Infinity;
        bVal = b.market_price_raw ?? -Infinity;
      } else if (sortColumn === 'finish') {
        const ORDER = { nonfoil: 0, foil: 1, etched: 2 };
        aVal = ORDER[a.finish] ?? 99;
        bVal = ORDER[b.finish] ?? 99;
      } else {
        aVal = a[sortColumn];
        bVal = b[sortColumn];
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      const aIsNum = typeof aVal === 'number';
      const bIsNum = typeof bVal === 'number';
      if (aVal == null) aVal = bIsNum ? -Infinity : '';
      if (bVal == null) bVal = aIsNum ? -Infinity : '';

      if (aVal < bVal) return sortType === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortType === 'asc' ? 1 : -1;
      return ai - bi;
    }).map(({ row }) => row);
  }, [filteredData, sortColumn, sortType]);

  const handleSortColumn = useCallback((col, type) => {
    setSort({ column: col, type });
    saveSort(col, type);
  }, []);

  const handleRowClick = (rowData) => {
    const key = rowData.row_key;
    setExpandedRowKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleCollectionClick = useCallback((e, collectionId) => {
    e.stopPropagation();
    navigate(`/library/${collectionId}`);
  }, [navigate]);

  const searchCards = useCallback(async (term) => {
    const res = await fetch(`${API_BASE}/cards/search/${encodeURIComponent(term)}`);
    return res.text();
  }, []);

  const handleAddCardClick = async () => {
    let collectionId = myCollectionId;
    if (!collectionId) {
      try {
        const res = await authFetch('/collections/my-collection');
        if (!res.ok) throw new Error('server error');
        const data = await res.json();
        setMyCollectionId(data.id);
        collectionId = data.id;
      } catch (err) {
        toaster.push(
          <Message type="error" showIcon closable>Failed to load My Collection. Please try again.</Message>,
          { placement: 'topCenter', duration: 4000 }
        );
        return;
      }
    }
    setSearchTerm('');
    setSearchModalOpen(true);
  };

  const handleSearchSelect = (card) => {
    setSearchModalOpen(false);
    setAddCardData({ scryfallId: card.id, cardName: card.name, card });
    setAddModalOpen(true);
  };

  const handleAddModalSuccess = () => {
    setAddModalOpen(false);
    setAddCardData(null);
    fetchAllData();
  };

  const handleAddModalClose = () => {
    setAddModalOpen(false);
    setAddCardData(null);
  };

  const handleEditClick = useCallback((e, loc, rowData) => {
    e.stopPropagation();
    setEditEntry({
      id: loc.entry_id,
      name: rowData.name,
      scryfall_id: rowData.scryfall_id,
      quantity: loc.quantity,
      condition: loc.condition,
      finish: rowData.finish,
      purchase_price: loc.purchase_price,
      notes: loc.notes || '',
      is_proxy: loc.is_proxy || false,
      is_not_owned: loc.is_not_owned || false,
      is_commander: loc.is_commander || false,
      is_sideboard: loc.is_sideboard || false,
      is_signature_spell: loc.is_signature_spell || false,
    });
    setEditCollectionMeta({
      collectionId: loc.collection_id,
      deckType: loc.deck_type || null,
      collectionType: loc.collection_type,
    });
    setEditModalOpen(true);
  }, []);

  const handleEditSuccess = useCallback((updatedValues = {}) => {
    setEditModalOpen(false);
    if (!editEntry || !editCollectionMeta) return;

    const entryId = editEntry.id;

    setTableData(prev => {
      return prev.reduce((acc, row) => {
        const locIdx = (row.locations || []).findIndex(l => l.entry_id === entryId);
        if (locIdx === -1) {
          acc.push(row);
          return acc;
        }

        const wasNotOwned = updatedValues?.is_not_owned;
        const newLocations = wasNotOwned
          ? row.locations.filter(l => l.entry_id !== entryId)
          : row.locations.map((l, i) => i === locIdx
            ? {
              ...l,
              quantity: updatedValues?.quantity ?? l.quantity,
              condition: updatedValues?.condition ?? l.condition,
              is_proxy: updatedValues?.is_proxy ?? l.is_proxy,
              purchase_price: updatedValues?.purchase_price ?? l.purchase_price,
              notes: updatedValues?.notes ?? l.notes,
              is_not_owned: false,
            }
            : l
          );

        if (newLocations.length === 0) return acc;

        const newRealQty = newLocations.reduce((s, l) => s + (l.is_proxy ? 0 : l.quantity), 0);
        const newProxyQty = newLocations.reduce((s, l) => s + (l.is_proxy ? l.quantity : 0), 0);
        const usdPrice = getUsdPrice(row.card_object?.prices, row.finish || 'nonfoil');
        const currentRaw = usdPrice ? Number(usdPrice) : null;
        const estimatedRaw = currentRaw != null ? currentRaw * newRealQty : null;

        acc.push({
          ...row,
          locations: newLocations,
          real_quantity: newRealQty,
          proxy_quantity: newProxyQty,
          locations_count: newLocations.length,
          estimated_value: estimatedRaw != null ? `$${estimatedRaw.toFixed(2)}` : '',
          estimated_value_raw: estimatedRaw,
        });
        return acc;
      }, []);
    });

    setEditEntry(null);
    setEditCollectionMeta(null);
  }, [editEntry, editCollectionMeta]);

  const handleEditClose = useCallback(() => {
    setEditModalOpen(false);
    setEditEntry(null);
    setEditCollectionMeta(null);
  }, []);

  const handleColumnVisibilityChange = useCallback((visibleKeys) => {
    const newHidden = new Set(
      OPTIONAL_DESKTOP_COLUMNS.map(c => c.key).filter(k => !visibleKeys.includes(k))
    );
    setHiddenColumns(newHidden);
    saveHiddenColumns(newHidden);
  }, []);

  const visibleOptionalKeys = useMemo(
    () => OPTIONAL_DESKTOP_COLUMNS.map(c => c.key).filter(k => !hiddenColumns.has(k)),
    [hiddenColumns]
  );

  const columns = useMemo(() => {
    if (isMobile) {
      return [
        { key: 'expand', label: '', width: 32 },
        { key: 'name', label: 'Name', flexGrow: 2, sortable: true },
        ...MOBILE_COLUMNS,
      ];
    }
    const cols = [{ key: 'expand', label: '', width: 32 }];
    if (!hiddenColumns.has('image')) cols.push({ key: 'image', label: '', width: 40 });
    cols.push({ key: 'name', label: 'Name', flexGrow: 2, sortable: true });
    cols.push(...OPTIONAL_DESKTOP_COLUMNS.filter(c => c.key !== 'image' && !hiddenColumns.has(c.key)));
    return cols;
  }, [hiddenColumns]);

  const summaryStats = useMemo(() => {
    const totalUnique = tableData.length;
    const totalReal = tableData.reduce((sum, card) => sum + (card.real_quantity || 0), 0);
    const totalProxy = tableData.reduce((sum, card) => sum + (card.proxy_quantity || 0), 0);
    const totalValue = tableData.reduce((sum, card) => {
      return sum + (card.estimated_value_raw || 0);
    }, 0);
    return { totalUnique, totalReal, totalProxy, totalValue };
  }, [tableData]);

  const renderRowExpanded = useCallback((rowData) => {
    const locations = rowData.locations || [];
    if (locations.length === 0) {
      return (
        <div style={{ padding: SPACING.expandedPadding, color: COLORS.muted, fontSize: SPACING.locationDetailFontSize }}>
          No location details available.
        </div>
      );
    }

    return (
      <div style={{ padding: SPACING.expandedPadding, backgroundColor: COLORS.expandedBg }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: SPACING.locationDetailFontSize }}>
          <thead>
            <tr style={{ color: COLORS.muted }}>
              <th style={{ textAlign: 'left', paddingBottom: 6, fontWeight: 'normal' }}>List</th>
              <th style={{ textAlign: 'left', paddingBottom: 6, fontWeight: 'normal' }}>Condition</th>
              <th style={{ textAlign: 'left', paddingBottom: 6, fontWeight: 'normal' }}>Qty</th>
              <th style={{ textAlign: 'left', paddingBottom: 6, fontWeight: 'normal' }}>Type</th>
              <th style={{ textAlign: 'right', paddingBottom: 6, fontWeight: 'normal' }}></th>
            </tr>
          </thead>
          <tbody>
            {locations.map((loc, i) => (
              <tr
                key={loc.entry_id || i}
                style={{ borderTop: `1px solid ${COLORS.locationRowBorder}` }}
              >
                <td style={{ padding: '6px 0' }}>
                  <span
                    style={{ color: COLORS.link, cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={(e) => handleCollectionClick(e, loc.collection_id)}
                  >
                    {loc.collection_name}
                  </span>
                </td>
                <td style={{ padding: '6px 8px', color: CONDITION_COLORS[loc.condition] || '#ddd' }}>
                  {CONDITION_LABELS[loc.condition] || loc.condition || '—'}
                </td>
                <td style={{ padding: '6px 8px', color: '#ddd' }}>
                  {loc.quantity}
                  {loc.is_proxy && (
                    <span style={{
                      marginLeft: 6,
                      color: PROXY_BADGE_COLOR,
                      fontSize: SPACING.locationBadgeFontSize,
                    }}>proxy</span>
                  )}
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <span style={{
                    backgroundColor: LOCATION_BADGE_COLORS[loc.collection_type] || '#555',
                    color: '#fff',
                    padding: `${SPACING.locationBadgePaddingV}px ${SPACING.locationBadgePaddingH}px`,
                    borderRadius: SPACING.locationBadgeRadius,
                    fontSize: SPACING.locationBadgeFontSize,
                  }}>
                    {LOCATION_BADGE_LABELS[loc.collection_type] || loc.collection_type}
                  </span>
                </td>
                <td style={{ padding: '6px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <span
                    style={{ color: COLORS.link, cursor: 'pointer', fontSize: SPACING.locationBadgeFontSize, marginRight: 12 }}
                    onClick={(e) => handleCollectionClick(e, loc.collection_id)}
                  >
                    Open
                  </span>
                  <span
                    style={{ color: COLORS.muted, cursor: 'pointer', fontSize: SPACING.editIconSize }}
                    onClick={(e) => handleEditClick(e, loc, rowData)}
                    title="Edit entry"
                  >
                    <EditIcon />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, [COLORS, handleCollectionClick, handleEditClick]);

  const getRowExpandedHeight = useCallback((rowData) => {
    const locations = (rowData.locations || []).length;
    return EXPANDED_ROW_PADDING * 2 + SPACING.expandedHeaderHeight + Math.max(locations, 1) * SPACING.expandedRowHeight;
  }, []);

  const getThumbnail = useCallback((rowData) => {
    const card = rowData.card_object;
    return card?.image_uris?.normal || card?.card_faces?.[0]?.image_uris?.normal || null;
  }, []);

  return (
    <>
      <div style={{ backgroundColor: '#282c34', minHeight: '100vh' }}>
        <NavigationBar />
        <Container style={{ maxWidth: SPACING.containerMaxWidth, margin: '0 auto', padding: SPACING.containerPadding, marginTop: SPACING.containerMarginTop }}>
          <Content>
            <div style={{ marginBottom: SPACING.headerMarginBottom, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <h2 style={{ margin: 0, color: '#fff' }}>Collection</h2>
                <p style={{ margin: '8px 0 0', color: COLORS.muted }}>
                  All owned cards across your decks and binders, grouped by printing and finish
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <Button appearance="primary" onClick={handleAddCardClick}>
                  Add Card
                </Button>
                <Button
                  appearance="ghost"
                  disabled={loading || loadingMore || enriching || refreshing}
                  onClick={() => setShowRefreshModal(true)}
                >
                  Refresh Prices
                </Button>
              </div>
            </div>
            {refreshing && (
              <div style={{ color: COLORS.muted, fontSize: 13, marginBottom: 8 }}>
                Refreshing prices... {refreshProgress.done} / {refreshProgress.total}
              </div>
            )}

            {summaryStats.totalUnique > 0 && (
              <Panel style={{ marginBottom: SPACING.containerPadding, backgroundColor: '#333' }}>
                <div style={{ display: 'flex', gap: SPACING.statsGap, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ color: COLORS.muted, fontSize: 12 }}>Unique Printings</div>
                    <div style={{ fontSize: SPACING.statFontSize, fontWeight: 'bold', color: '#fff' }}>
                      {summaryStats.totalUnique}{!allLoaded && '+'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: COLORS.muted, fontSize: 12 }}>Real Copies</div>
                    <div style={{ fontSize: SPACING.statFontSize, fontWeight: 'bold', color: COLORS.gain }}>
                      {summaryStats.totalReal}{!allLoaded && '+'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: COLORS.muted, fontSize: 12 }}>Proxy Copies</div>
                    <div style={{ fontSize: SPACING.statFontSize, fontWeight: 'bold', color: PROXY_BADGE_COLOR }}>
                      {summaryStats.totalProxy}{!allLoaded && '+'}
                    </div>
                    <div style={{ color: COLORS.muted, fontSize: SPACING.locationBadgeFontSize }}>not included in value</div>
                  </div>
                  <div>
                    <div style={{ color: COLORS.muted, fontSize: 12 }}>Est. Value (real)</div>
                    <div style={{ fontSize: SPACING.statFontSize, fontWeight: 'bold', color: '#fff' }}>
                      ${summaryStats.totalValue.toFixed(2)}{!allLoaded && '+'}
                    </div>
                  </div>
                </div>
              </Panel>
            )}

            {error && (
              <Message type="error" style={{ marginBottom: 16 }}>
                {error}
              </Message>
            )}

            <FlexboxGrid
              style={{
                padding: `${SPACING.filterPaddingV}px 0`,
                gap: SPACING.filterGap,
                flexWrap: 'wrap',
                alignItems: 'center',
                marginBottom: SPACING.filterMarginBottom,
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <FlexboxGrid.Item style={isMobile ? { width: '100%' } : undefined}>
                <Input
                  placeholder="Search by name..."
                  value={searchInput}
                  onChange={setSearchInput}
                  style={{ width: SPACING.searchWidth }}
                />
              </FlexboxGrid.Item>
              <FlexboxGrid.Item style={isMobile ? { width: '100%' } : undefined}>
                <CheckPicker
                  data={COLOR_OPTIONS}
                  value={colorFilter}
                  onChange={setColorFilter}
                  placeholder="Colors"
                  style={{ width: SPACING.pickerWidth }}
                  searchable={false}
                  block={isMobile}
                />
              </FlexboxGrid.Item>
              <FlexboxGrid.Item style={isMobile ? { width: '100%' } : undefined}>
                <CheckPicker
                  data={RARITY_OPTIONS}
                  value={rarityFilter}
                  onChange={setRarityFilter}
                  placeholder="Rarity"
                  style={{ width: SPACING.pickerWidth }}
                  searchable={false}
                  block={isMobile}
                />
              </FlexboxGrid.Item>
              <FlexboxGrid.Item style={isMobile ? { width: '100%' } : undefined}>
                <CheckPicker
                  data={collectionOptions}
                  value={collectionFilter}
                  onChange={setCollectionFilter}
                  placeholder="Collection"
                  style={{ width: SPACING.collectionPickerWidth }}
                  searchable={false}
                  block={isMobile}
                />
              </FlexboxGrid.Item>
              <FlexboxGrid.Item style={isMobile ? { width: '100%' } : undefined}>
                <RadioGroup
                  inline
                  value={proxyFilter}
                  onChange={setProxyFilter}
                  appearance="picker"
                >
                  <Radio value="all">All</Radio>
                  <Radio value="real">Real</Radio>
                  <Radio value="proxy">Proxy</Radio>
                </RadioGroup>
              </FlexboxGrid.Item>
              {!isMobile && (
                <FlexboxGrid.Item>
                  <CheckPicker
                    data={COLUMN_PICKER_DATA}
                    value={visibleOptionalKeys}
                    onChange={handleColumnVisibilityChange}
                    placeholder="Columns"
                    style={{ width: SPACING.columnPickerWidth }}
                    searchable={false}
                    countable={false}
                  />
                </FlexboxGrid.Item>
              )}
              {hasActiveFilters && (
                <FlexboxGrid.Item>
                  <Button appearance="ghost" onClick={handleClearFilters} size="sm">
                    Clear Filters
                  </Button>
                </FlexboxGrid.Item>
              )}
              {hasActiveFilters && (
                <FlexboxGrid.Item>
                  <span style={{ color: COLORS.muted, fontSize: SPACING.filterCountFontSize }}>
                    Showing {sortedData.length} of {tableData.length}
                  </span>
                </FlexboxGrid.Item>
              )}
              {(loadingMore || enriching) && (
                <FlexboxGrid.Item>
                  <span style={{ color: COLORS.muted }}>
                    {loadingMore
                      ? `Fetching page ${paginationProgress} of ${totalPages}...`
                      : 'Enriching card data...'}
                  </span>
                </FlexboxGrid.Item>
              )}
            </FlexboxGrid>

            {(loading || loadingMore) && tableData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60 }}>
                <Loader size="lg" content={
                  loading ? 'Loading...'
                    : `Fetching page ${paginationProgress} of ${totalPages}...`
                } />
              </div>
            ) : !loading && !loadingMore && !enriching && tableData.length === 0 ? (
              <Panel>
                <Message type="info">
                  You don't have any owned cards in your collection yet.
                </Message>
              </Panel>
            ) : (
              <Table
                data={sortedData}
                height={windowHeight - TABLE_TOP_OFFSET}
                virtualized
                shouldUpdateScroll={false}
                rowHeight={TABLE_ROW_HEIGHT}
                onSortColumn={handleSortColumn}
                sortColumn={sortColumn}
                sortType={sortType}
                onRowClick={handleRowClick}
                rowKey="row_key"
                expandedRowKeys={expandedRowKeys}
                renderRowExpanded={renderRowExpanded}
                rowExpandedHeight={getRowExpandedHeight}
                loading={enriching}
                style={{ backgroundColor: '#222' }}
              >
                {columns.map((col) => (
                  <Column
                    key={col.key}
                    flexGrow={col.flexGrow}
                    width={col.width}
                    sortable={col.sortable}
                  >
                    <HeaderCell>{col.label}</HeaderCell>
                    {col.key === 'expand' ? (
                      <Cell>
                        {(rowData) => {
                          const isExpanded = expandedRowKeys.includes(rowData.row_key);
                          return isExpanded
                            ? <ArrowDownIcon style={{ color: COLORS.muted }} />
                            : <ArrowRightIcon style={{ color: COLORS.muted }} />;
                        }}
                      </Cell>
                    ) : col.key === 'image' ? (
                      <Cell>
                        {(rowData) => {
                          const thumb = getThumbnail(rowData);
                          return thumb ? (
                            <img
                              src={thumb}
                              alt={rowData.name}
                              style={{ height: SPACING.thumbnailSize, width: 'auto', borderRadius: 3, display: 'block' }}
                            />
                          ) : null;
                        }}
                      </Cell>
                    ) : col.key === 'name' ? (
                      <Cell dataKey="name">
                        {(rowData) => (
                          <span style={{ color: COLORS.link, cursor: 'pointer' }}>
                            {rowData.name}
                          </span>
                        )}
                      </Cell>
                    ) : col.key === 'finish' ? (
                      <Cell dataKey="finish">
                        {(rowData) => (
                          <span style={{ color: FINISH_COLORS[rowData.finish] || '#aaa' }}>
                            {FINISH_LABELS[rowData.finish] || rowData.finish}
                          </span>
                        )}
                      </Cell>
                    ) : col.key === 'market_price' ? (
                      <Cell dataKey="market_price">
                        {(rowData) => rowData.market_price || '—'}
                      </Cell>
                    ) : col.key === 'estimated_value' ? (
                      <Cell dataKey="estimated_value">
                        {(rowData) => rowData.estimated_value || '—'}
                      </Cell>
                    ) : (
                      <Cell dataKey={col.key} />
                    )}
                  </Column>
                ))}
              </Table>
            )}
          </Content>
        </Container>
      </div>
      <Modal open={searchModalOpen} onClose={() => setSearchModalOpen(false)} size={isMobile ? 'full' : 'md'} backdrop="static">
        <Modal.Header>
          <Modal.Title>Search Cards to Add</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SearchBar
            term={searchTerm}
            setTerm={setSearchTerm}
            retrieve={searchCards}
            onSelect={handleSearchSelect}
            placeholder="Enter a card name. Minimum 2 characters to search"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setSearchModalOpen(false)} appearance="subtle">Close</Button>
        </Modal.Footer>
      </Modal>
      <Modal open={showRefreshModal} onClose={() => setShowRefreshModal(false)} backdrop="static">
        <Modal.Header>
          <Modal.Title>Refresh All Prices</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This will request updated prices for all <strong>{[...new Set(tableData.map(e => e.scryfall_id))].filter(Boolean).length}</strong> cards in your collection.</p>
          <p>Cards refreshed in the last 6 hours will be skipped automatically. For large collections this may take several minutes.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowRefreshModal(false)} appearance="subtle">Cancel</Button>
          <Button onClick={handleRefreshPrices} appearance="primary">Start Refresh</Button>
        </Modal.Footer>
      </Modal>
      <AddToCollectionModal
        open={addModalOpen}
        onClose={handleAddModalClose}
        onSuccess={handleAddModalSuccess}
        scryfallId={addCardData?.scryfallId}
        cardName={addCardData?.cardName}
        card={addCardData?.card}
        preSelectedCollectionId={myCollectionId}
      />
      <EditEntryModal
        open={editModalOpen}
        onClose={handleEditClose}
        entry={editEntry}
        collectionId={editCollectionMeta?.collectionId}
        deckType={editCollectionMeta?.deckType}
        collectionType={editCollectionMeta?.collectionType}
        onUpdated={handleEditSuccess}
      />
    </>
  );
};

export default GrandCollection;
